import { PointCloudObject } from "@/object-cache";
import { isCloseTo } from "@faro-lotv/foundation";
import {
  LodTreeNode,
  SupportedCamera,
  memberWithPrivateData,
} from "@faro-lotv/lotv";
import { Size, useFrame, useThree } from "@react-three/fiber";
import { throttle } from "es-toolkit";
import { useEffect, useMemo, useRef } from "react";
import { Box3, Matrix4 } from "three";
import {
  pointOpacityCurve,
  useActivePointOpacitySettings,
} from "./point-opacity-settings";
import { invalidateSceneEvent } from "./revision-rendering-pipeline";

/** Point opacity to use when dynamic opacity scaling is disabled. */
const FALLBACK_OPACITY = 0.1;

/**
 * Set the opacity of the point clouds dynamically for best visual quality.
 *
 * The goal is to keep walls and other features clearly visible even when zoomed out,
 * while still making individual points visible when zoomed in.
 *
 * @param pointClouds The point clouds to adapt the opacity for.
 * @param enabled Whether to enable the dynamic opacity scaling.
 *  If disabled, falls back to a fixed point opacity.
 *  Defaults to true.
 */
export function useScaledPointOpacity(
  pointClouds: PointCloudObject[],
  enabled: boolean = true,
): void {
  const camera = useThree((three) => three.camera);
  const size = useThree((three) => three.size);

  const hasUsedSubsampledRenderingRef = useRef(false);

  const pointOpacitySettings = useActivePointOpacitySettings();
  const opacityFunction = useMemo(
    () => pointOpacityCurve(pointOpacitySettings),
    [pointOpacitySettings],
  );

  // Update point opacity based on camera and point cloud visibility
  useFrame(() => {
    // Throttled to avoid performance issues
    calculateAllPointOpacities(
      enabled,
      camera,
      size,
      pointClouds,
      opacityFunction,
    );

    const isUsingSubsampledRendering = !!pointClouds
      .at(0)
      ?.getSubsampledRenderingOn();

    // If subsampled rendering is enabled/disabled, update the opacity immediately
    // This balances out big visual differences due to less points being rendered
    if (hasUsedSubsampledRenderingRef.current !== isUsingSubsampledRendering) {
      hasUsedSubsampledRenderingRef.current = isUsingSubsampledRendering;
      calculateAllPointOpacities.flush();
    }
  });

  // Refresh the scene when rendering strategy changes
  useEffect(() => {
    calculateAllPointOpacities.flush();
    invalidateSceneEvent.emit();
  }, [enabled, pointOpacitySettings]);
}

const SET_OPACITY_THROTTLE_MS = 500;

/**
 * Determines the opacity for all point clouds based on the point density.
 * If `isScaledOpacityEnabled` is false, a fixed fallback value is used.
 */
const calculateAllPointOpacities = throttle(
  (
    isScaledOpacityEnabled: boolean,
    camera: SupportedCamera,
    size: Size,
    pointClouds: PointCloudObject[],
    opacityFunction: (pointsPerPixel: number) => number,
  ): void => {
    // NDC goes from -1 to 1, so 2 on each axis
    const ndcToPixels = Math.sqrt(size.width * size.height) / 2;

    for (const pointCloud of pointClouds.values()) {
      if (isScaledOpacityEnabled) {
        calculatePointsPerPixel(
          camera,
          pointCloud,
          ndcToPixels,
          opacityFunction,
        );
      } else {
        updateOpacityIfNeeded(pointCloud, FALLBACK_OPACITY);
      }
    }
  },
  SET_OPACITY_THROTTLE_MS,
);

/**
 * Calculate and set the opacity for the given point cloud.
 * Tries to find opacity values to keep walls and other features clearly visible.
 *
 * @param camera The camera to use for the calculation.
 * @param pointCloudObject The point cloud object to set the opacity for.
 * @param ndcToPixels The conversion factor from NDC to pixels.
 * @param opacityFunction The function to calculate the opacity based on points per pixel.
 */
function calculatePointsPerPixel(
  camera: SupportedCamera,
  pointCloudObject: PointCloudObject,
  ndcToPixels: number,
  opacityFunction: (pointsPerPixel: number) => number,
): void {
  const renderedNodeIds = [...pointCloudObject.nodesInGPU.keys()];

  const rawPointsPerNdc = maxPointsPerNdc(
    camera,
    pointCloudObject,
    renderedNodeIds,
    pointCloudObject.tree.root,
    0,
  );

  // If subsampling is enabled, render the points at higher opacity make the visuals more consistent
  const subsamplingFactor = pointCloudObject.getSubsampledRenderingOn()
    ? pointCloudObject.getSubsampledRenderingFraction()
    : 1;

  // From points / NDC to points / pixel, normalizing for the subsampling factor
  const pointsPerPixel = (subsamplingFactor * rawPointsPerNdc) / ndcToPixels;
  // Store points per pixel data for the rendering debug overlay
  pointCloudObject.userData.pointsPerPixel = pointsPerPixel;

  const opacity = opacityFunction(pointsPerPixel);

  updateOpacityIfNeeded(pointCloudObject, opacity);
}

/**
 * Recursively calculate the "branch" in the point cloud tree with the highest total point density.
 *
 * @param camera The active camera, used to calculate the point density for the current view.
 * @param pointCloud The point cloud object to calculate the point density for.
 * @param renderedNodeIds The IDs of the nodes that are actually displayed to the user.
 * @param node The current node to start the calculation from.
 * @param parentValue The point density of the parent node, used to calculate the total density.
 * @returns The maximum point density, in normalized device coordinates, for the given node and its children.
 */
const maxPointsPerNdc = memberWithPrivateData(() => {
  // Cached allocations
  const ndcBbox = new Box3();
  const projectionMatrix = new Matrix4();

  return (
    camera: SupportedCamera,
    pointCloud: PointCloudObject,
    renderedNodeIds: number[],
    node: LodTreeNode,
    parentValue: number,
  ): number => {
    // Approximate how much space of the view is occupied by the current node
    ndcBbox.copy(node.boundingBox);
    ndcBbox.applyMatrix4(
      projectionMatrix.multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse,
      ),
    );

    const points = node.numPoints;
    const ndc = ndcBbox.min.distanceTo(ndcBbox.max);

    const pointsPerNdc = points > 0 ? points / ndc : 0;
    const totalPointsPerNdc = parentValue + pointsPerNdc;

    // ASSUMPTION: The bounding box of the parent node contains the bounding boxes of all children
    // ASSUMPTION: If a parent node is not rendered, its children are not rendered either
    const childrenPointsPerNdc = node.children
      .filter((child) => renderedNodeIds.includes(child.id))
      .map((child) =>
        maxPointsPerNdc(
          camera,
          pointCloud,
          renderedNodeIds,
          child,
          totalPointsPerNdc,
        ),
      );

    if (childrenPointsPerNdc.length === 0) return totalPointsPerNdc;

    return Math.max(...childrenPointsPerNdc);
  };
});

function updateOpacityIfNeeded(
  pointCloudObject: PointCloudObject,
  opacity: number,
): void {
  if (isCloseTo(pointCloudObject.material.opacity, opacity)) return;

  pointCloudObject.material.opacity = opacity;
  pointCloudObject.material.needsUpdate = true;
}
